import React, { useEffect, useState, useRef } from 'react';
import { formatCurrency, formatNumber, parseNumber } from '../utils/format';
import { PencilIcon } from 'lucide-react';
type FieldType = 'currency' | 'text' | 'number' | 'percent';
interface EditableFieldProps {
  value: string | number | null;
  type: FieldType;
  onSave: (value: any) => void;
  className?: string;
  inputClassName?: string;
  decimals?: number;
  isNegativeRed?: boolean;
}
export function EditableField({
  value,
  type,
  onSave,
  className = '',
  inputClassName = '',
  decimals = 2,
  isNegativeRed = false
}: EditableFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      // Select all text when focusing
      inputRef.current.select();
    }
  }, [isEditing]);
  const handleEditClick = () => {
    // Prepare the raw value for editing
    if (value === null) {
      setEditValue('');
    } else if (type === 'currency' || type === 'number') {
      setEditValue(value.toString());
    } else if (type === 'percent') {
      // Remove the % sign if it's a string, or just use the value
      const strVal = value.toString();
      setEditValue(strVal.replace('%', ''));
    } else {
      setEditValue(value.toString());
    }
    setIsEditing(true);
  };
  const handleSave = () => {
    setIsEditing(false);
    if (editValue.trim() === '') {
      if (type === 'text') {
        onSave('');
      } else {
        onSave(null);
      }
      return;
    }
    if (type === 'currency' || type === 'number') {
      const parsed = parseNumber(editValue);
      onSave(parsed);
    } else if (type === 'percent') {
      // If they didn't type a %, we'll add it back in the display, but save the raw string or number
      // Assuming percent is stored as a string like "4.375%"
      const parsed = parseNumber(editValue);
      if (parsed !== null) {
        // Format to 3 decimal places for rates usually
        onSave(`${parsed.toFixed(3)}%`);
      } else {
        onSave(editValue);
      }
    } else {
      onSave(editValue);
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
    }
  };
  // Format the display value
  let displayValue: React.ReactNode = '—';
  let isNegative = false;
  if (value !== null && value !== undefined && value !== '') {
    if (type === 'currency') {
      displayValue = formatCurrency(value as number);
      isNegative = value as number < 0;
    } else if (type === 'number') {
      displayValue = formatNumber(value as number, decimals);
      isNegative = value as number < 0;
    } else {
      displayValue = value;
    }
  }
  const textColorClass = isNegative && isNegativeRed ? 'text-red-600' : '';
  if (isEditing) {
    return <input ref={inputRef} type={type === 'text' ? 'text' : 'text'} // Use text to allow formatting characters during typing
    value={editValue} onChange={e => setEditValue(e.target.value)} onBlur={handleSave} onKeyDown={handleKeyDown} className={`bg-white border-2 border-blue-400 rounded px-1 py-0.5 outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-500 w-full min-w-[60px] text-gray-900 ${inputClassName}`} style={{
      width: `${Math.max(editValue.length + 2, 4)}ch`
    }} data-id="element-33" />;
  }
  return <span onClick={handleEditClick} className={`group relative cursor-text inline-flex items-center border-b border-dashed border-gray-300 hover:border-gray-500 hover:bg-gray-100/50 rounded-sm transition-colors ${textColorClass} ${className}`} title="Click to edit" data-id="element-34">
      {displayValue}
      <PencilIcon className="w-3 h-3 opacity-0 group-hover:opacity-50 ml-1 absolute -right-4 transition-opacity" data-id="element-35" />
    </span>;
}